import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  avatarUrl: string | null;
  bio: string | null;
  coinsBalance: number;
  xp: number;
  level: number;
  readingStreak: number;
  emailVerified: boolean;
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthToken = params.get('token');
    if (oauthToken) {
      localStorage.setItem('scribehaven_token', oauthToken);
      window.history.replaceState({}, '', window.location.pathname);
    }

    const token = localStorage.getItem('scribehaven_token');
    if (token) {
      api.auth.me()
        .then(setUser)
        .catch(() => localStorage.removeItem('scribehaven_token'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const onFocus = () => {
      const token = localStorage.getItem('scribehaven_token');
      if (token) {
        api.auth.me().then(setUser).catch(() => {});
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const data = await api.auth.login({ email, password });
    localStorage.setItem('scribehaven_token', data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (username: string, email: string, password: string, isMature = false) => {
    const data = await api.auth.register({ username, email, password, isMature });
    localStorage.setItem('scribehaven_token', data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('scribehaven_token');
    setUser(null);
  }, []);

  return { user, loading, login, register, logout, setUser };
}
